import { gql } from "@apollo/client";
import { AccountFragment } from "fragments/account";

// One fragment for every screen. A file row is a dozen short values and the
// only unbounded field on it is `metadata`, which the details screen reads and
// the listing does not — so the split sections need would buy a few hundred
// bytes and cost a second fragment to keep in step.
//
// `metadata` is on it anyway, because the listing is what the upload screen
// returns to and a file whose derived sizes have just landed should not need a
// second round trip to show them.
export const SiteFileFragment = gql`
  fragment SiteFileFragment on SiteFile {
    id
    fileId
    bucketId
    siteId
    kind
    name
    visibility
    hash
    ext
    size
    status
    metadata
    description
    tags
    createdAt
    updatedAt
  }
`;

// Through the site, like everything else beneath one. Here the nesting is doing
// more than it does for pages: the BYTES are in bucketws, which pagews reaches
// with a single service key, so bucketws cannot tell one pagews tenant from
// another. This path is the entire tenancy boundary — see model/site-file.
export const list = gql`
  query SiteFileList($siteId: String!, $kind: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            id
            files {
              id
              list(kind: $kind) {
                id
                last
                items {
                  ...SiteFileFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${SiteFileFragment}
`;

export const get = gql`
  query SiteFileGet($siteId: String!, $kind: String!, $fileId: String!) {
    viewer {
      id
      account {
        ...AccountFragment
        sites {
          id
          get(siteId: $siteId) {
            id
            files {
              id
              get(kind: $kind, fileId: $fileId) {
                ...SiteFileFragment
              }
            }
          }
        }
      }
    }
  }
  ${AccountFragment}
  ${SiteFileFragment}
`;

// Answers with a presigned POST form AND the row. The upload is not finished
// when this returns — the browser still has to post the form to S3 itself. See
// the upload screen, and model/site-file.create on the API side.
export const create = gql`
  mutation SiteFileNew($siteId: String!, $kind: String!, $input: SiteFileInputCreate!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            files {
              create(kind: $kind, input: $input) {
                form
                file {
                  ...SiteFileFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${SiteFileFragment}
`;

export const edit = gql`
  mutation SiteFileEdit($siteId: String!, $kind: String!, $fileId: String!, $input: SiteFileInputUpdate!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            files {
              get(kind: $kind, fileId: $fileId) {
                update(input: $input) {
                  ...SiteFileFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${SiteFileFragment}
`;

// Copies size, status and metadata across from bucketws.
//
// A mutation because it writes, and it has to exist because nothing tells this
// service when an upload finished: bucketws fills those fields in from its own
// S3 event handler, after the create mutation has already answered. The details
// screen fires it on open and the upload screen fires it once S3 accepts the
// post.
export const sync = gql`
  mutation SiteFileSync($siteId: String!, $kind: String!, $fileId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            files {
              get(kind: $kind, fileId: $fileId) {
                sync {
                  ...SiteFileFragment
                }
              }
            }
          }
        }
      }
    }
  }
  ${SiteFileFragment}
`;

export const remove = gql`
  mutation SiteFileRemove($siteId: String!, $kind: String!, $fileId: String!) {
    viewer {
      account {
        sites {
          get(siteId: $siteId) {
            files {
              get(kind: $kind, fileId: $fileId) {
                remove
              }
            }
          }
        }
      }
    }
  }
`;

// Every mutation here changes what a listing shows — a create adds a row, an
// edit changes its description, a sync changes its status and size, a remove
// takes it away — so all of them evict the same keys.
//
// The literal has to match what `SiteFileQueries.id` resolves to on the API
// side, which is `${siteId}/files/queries`. Matching on the suffix rather than
// the whole thing is deliberate: it evicts BOTH kinds' listings for every site
// in the cache, and the two kinds share nothing, so the alternative would be a
// key built per screen for no benefit. If this literal ever drifts from the
// resolver the mutation still succeeds and the list keeps showing the old
// state — the silent staleness the page and section specs exist to catch.
export const update = () => (cache) => {
  Object.keys(cache.data.data).forEach((key) => {
    if (key.includes(`files/queries`)) {
      cache.data.delete(key);
    }
  });
};
